
import React, { useState } from 'react';
import { ViewState } from '../types';
import Logo from './Logo';

interface HeaderProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  customLogoUrl?: string;
  isLoggedIn?: boolean;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ currentView, setView, customLogoUrl, isLoggedIn, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems: { label: string; value: ViewState }[] = [
    { label: 'Home', value: 'home' },
    { label: 'About Us', value: 'about' },
    { label: 'Projects', value: 'projects' },
    { label: 'Careers', value: 'careers' },
    { label: 'Clients', value: 'clients' },
    { label: 'Contact', value: 'contact' },
    { label: 'AI Advisor', value: 'ai-advisor' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white backdrop-blur-md border-b border-slate-200 shadow-sm transition-all h-16 flex items-center">
      <div className="max-w-7xl mx-auto px-4 w-full flex items-center justify-between">
        <div 
          className="flex items-center gap-3 cursor-pointer group" 
          onClick={() => setView('home')}
        >
          <Logo className="h-9 w-auto" customSrc={customLogoUrl} />
          <div className="flex flex-col leading-none ml-1">
            <span className="font-black text-blue-900 tracking-tighter text-sm sm:text-base uppercase">SAI SSK</span>
            <span className="text-[8px] font-bold text-slate-400 tracking-[0.2em] uppercase leading-tight">Infrastructures</span>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1 lg:gap-3">
          {navItems.map((item) => (
            <button
              key={item.value}
              onClick={() => setView(item.value)}
              className={`px-3 py-2 text-xs lg:text-sm font-bold transition-all rounded-lg whitespace-nowrap ${
                currentView === item.value 
                ? 'bg-blue-50 text-blue-700' 
                : 'text-slate-500 hover:bg-slate-50 hover:text-blue-600'
              }`}
            >
              {item.label}
            </button>
          ))}
          {isLoggedIn && onLogout && (
            <button
              onClick={onLogout}
              className="px-3 py-2 text-xs lg:text-sm font-bold text-red-600 hover:bg-red-50 transition-all rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              Sign Out
            </button>
          )}
        </nav>

        {/* Mobile Controls */}
        <div className="flex md:hidden items-center gap-1">
          <button 
            className={`p-2 rounded-xl border transition-all duration-200 shadow-sm ${
              isMenuOpen 
                ? 'bg-blue-700 border-blue-700 text-white' 
                : 'bg-slate-100 border-slate-200 text-blue-900 hover:bg-slate-200'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 z-[100] bg-white md:hidden p-6 flex flex-col gap-6 shadow-2xl border-t border-slate-100 min-h-screen overflow-y-auto">
          <div className="flex flex-col gap-2">
            {navItems.map((item) => (
              <button
                key={item.value}
                onClick={() => {
                  setView(item.value);
                  setIsMenuOpen(false);
                }}
                className={`text font-black uppercase tracking-tighter text-left py-4 px-4 rounded-xl transition-all ${
                  currentView === item.value 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'text-slate-900 hover:bg-slate-50'
                }`}
              >
                {item.label}
              </button>
            ))}
            {isLoggedIn && onLogout && (
              <button
                onClick={() => {
                  onLogout();
                  setIsMenuOpen(false);
                }}
                className="text-red-600 font-black uppercase tracking-tighter text-left py-4 px-4 rounded-xl hover:bg-red-50 transition-all flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                Sign Out
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
